export interface Environment {
  name: string;
  production: boolean;
  signalRUrl: string;
  pwa: {
    enabled: boolean;
  };
  msalConfig: {
    clientId: string;
    authority: string;
    redirectUri: string;
    graphUrl: string;
    apiScope: string;
  };
  apiUrl?: string;
  powerBiApiUrl?: string;
  features: {
    debug: boolean;
    analytics: boolean;
  };
  cache: {
    enabled: boolean;
    version: string;
    defaultTTL: number;
    cargoList: number;
    cargoCount: number;
    baltic: number;
    bunkerPrices: number;
    fixedDays: number;
    fixtures: number;
    positionList: number;
    currentUser: number;
    poolReports: number;
    annualReport: number;
    monthlyReport: number;
    dailyReports: number;
  };
}

// Helper function to get environment variables with fallbacks
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return import.meta.env[key] || defaultValue;
};

const getBooleanEnvVar = (key: string, defaultValue: boolean = false): boolean => {
  const value = import.meta.env[key];
  if (value === undefined || value === null) return defaultValue;
  return value === 'true' || value === '1';
};

const getNumberEnvVar = (key: string, defaultValue: number): number => {
  const value = import.meta.env[key];
  if (value === undefined || value === null) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

// Declare global build timestamp
declare const __BUILD_TIMESTAMP__: string;

const getDefaultSignalRUrl = (): string => {
  const apiUrl = getEnvVar('VITE_API_URL', 'https://extranetapidev.azurewebsites.net/api');
  return apiUrl.replace(/\/v1\/?$/i, '');
};

export const environment: Environment = {
  name: getEnvVar('VITE_ENV_NAME', 'local'),
  production: getEnvVar('VITE_NODE_ENV', 'development') === 'production',
  signalRUrl: getEnvVar('VITE_SIGNALR_URL', getDefaultSignalRUrl()),
  pwa: {
    enabled: getBooleanEnvVar('VITE_PWA_ENABLED', true),
  },
  msalConfig: {
    clientId: getEnvVar('VITE_MSAL_CLIENT_ID', 'b3ce1c45-ee89-4e3f-89e3-4ef07ac8a33f'),
    authority: getEnvVar(
      'VITE_MSAL_AUTHORITY',
      'https://login.microsoftonline.com/dbf6b26c-933f-4b4e-bbf9-6458c61a9db1'
    ),
    redirectUri: getEnvVar('VITE_MSAL_REDIRECT_URI', 'http://localhost:4200'),
    graphUrl: getEnvVar('VITE_GRAPH_URL', 'https://graph.microsoft.com/v1.0'),
    apiScope: getEnvVar(
      'VITE_MSAL_API_SCOPE',
      'api://b3ce1c45-ee89-4e3f-89e3-4ef07ac8a33f/access_api'
    ),
  },
  apiUrl: getEnvVar('VITE_API_URL', 'https://extranetapidev.azurewebsites.net/api/v1'),
  powerBiApiUrl: getEnvVar(
    'VITE_POWERBI_API_URL',
    'https://powerbiembeddeddev.azurewebsites.net/api/'
  ),
  features: {
    debug: getBooleanEnvVar('VITE_ENABLE_DEBUG', false),
    analytics: getBooleanEnvVar('VITE_ENABLE_ANALYTICS', false),
  },
  cache: {
    enabled: getBooleanEnvVar('VITE_CACHE_ENABLED', true),
    version:
      typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : Date.now().toString(),
    defaultTTL: getNumberEnvVar('VITE_CACHE_DEFAULT_TTL', 1800000), // 30 min
    cargoList: getNumberEnvVar('VITE_CACHE_CARGO_LIST_TTL', 1800000), // 30 min
    cargoCount: getNumberEnvVar('VITE_CACHE_CARGO_COUNT_TTL', 300000), // 5 min
    baltic: getNumberEnvVar('VITE_CACHE_BALTIC_TTL', 300000), // 5 min
    bunkerPrices: getNumberEnvVar('VITE_CACHE_BUNKER_PRICES_TTL', 1800000), // 30 min
    fixedDays: getNumberEnvVar('VITE_CACHE_FIXED_DAYS_TTL', 300000), // 5 min
    fixtures: getNumberEnvVar('VITE_CACHE_FIXTURES_TTL', 1800000), // 30 min
    positionList: getNumberEnvVar('VITE_CACHE_POSITION_LIST_TTL', 1800000), // 30 min
    currentUser: getNumberEnvVar('VITE_CACHE_CURRENT_USER_TTL', 300000), // 5 min
    poolReports: getNumberEnvVar('VITE_CACHE_POOL_REPORTS_TTL', 1800000), // 30 min
    annualReport: getNumberEnvVar('VITE_CACHE_ANNUAL_REPORT_TTL', 1800000), // 30 min
    monthlyReport: getNumberEnvVar('VITE_CACHE_MONTHLY_REPORT_TTL', 1800000), // 30 min
    dailyReports: getNumberEnvVar('VITE_CACHE_DAILY_REPORTS_TTL', 300000), // 5 min
  },
};
